import React, { useMemo, useRef, useState } from 'react';
import {
  FolderOpen as ExpandAllIcon,
  FolderMinus as CollapseAllIcon,
  ZoomIn,
  ZoomOut,
  Play,
  Wrench,
  X,
  Info,
  Layers,
  GripVertical,
  Move,
  Sparkles
} from 'lucide-react';

// Helper: map entity.type to high-level group
const getGroupForType = (type) => {
  const characterTypes = ['protagonist', 'helper', 'sacrificial', 'community'];
  const placeTypes = ['origin', 'destination', 'habitat', 'obstacle', 'dwelling', 'setting'];
  const objectTypes = ['life_force', 'essence', 'legacy', 'bond', 'theme', 'artifact', 'symbol'];
  if (characterTypes.includes(type)) return 'characters';
  if (placeTypes.includes(type)) return 'places';
  if (objectTypes.includes(type)) return 'objects';
  return 'others';
};

const defaultGroupsExpanded = { characters: true, places: true, objects: true, relationships: true };

const KnowledgeGraphToolbox = ({ graphData, onRun, language = 'traditional' }) => {
  const [groupsExpanded, setGroupsExpanded] = useState(defaultGroupsExpanded);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const workspaceRef = useRef(null);

  // Workspace model
  const [workspaceNodes, setWorkspaceNodes] = useState([]); // {id, name, type, x, y}
  const [workspaceLinks, setWorkspaceLinks] = useState([]); // user-created links (none by default)

  const t = useMemo(() => ({
    title: '工具箱',
    run: '執行',
    expandAll: '展開全部',
    collapseAll: '收合全部',
    characters: '人物',
    places: '地點',
    objects: '物品',
    relationships: '關係',
    inspector: '屬性編輯',
    name: '名稱',
    type: '類型',
    description: '描述',
    importance: '重要性',
    hintDragGroup: '拖放整組到工作區',
    hintDragItem: '拖放單一實體到工作區',
  }), [language]);

  const grouped = useMemo(() => {
    const groups = { characters: [], places: [], objects: [] };
    (graphData.nodes || []).forEach((n) => {
      const g = getGroupForType(n.type);
      if (groups[g]) groups[g].push(n);
    });
    return groups;
  }, [graphData]);

  const relationshipList = useMemo(() => graphData.links || [], [graphData]);

  const setAllExpanded = (expanded) => {
    setGroupsExpanded({ characters: expanded, places: expanded, objects: expanded, relationships: expanded });
  };

  const getTypeChipClasses = (type) => {
    const group = getGroupForType(type);
    switch (group) {
      case 'characters':
        return 'bg-emerald-100 text-emerald-700';
      case 'places':
        return 'bg-sky-100 text-sky-700';
      case 'objects':
        return 'bg-violet-100 text-violet-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Drag helpers
  const handleEntityDragStart = (entity, e) => {
    e.dataTransfer.setData('application/x-kg-entity', JSON.stringify({ kind: 'entity', entityId: entity.id }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleGroupDragStart = (groupKey, e) => {
    e.dataTransfer.setData('application/x-kg-group', JSON.stringify({ kind: 'group', groupKey }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const addNodesToWorkspace = (nodesToAdd, dropPoint) => {
    const baseX = (dropPoint?.x ?? 140) - pan.x;
    const baseY = (dropPoint?.y ?? 140) - pan.y;
    // Stacked layout offsets for a compact fan display similar to reference UI
    const offset = 18;
    const currentIds = new Set(workspaceNodes.map((n) => n.id));
    const newNodes = [];
    nodesToAdd.forEach((n, idx) => {
      if (!currentIds.has(n.id)) {
        newNodes.push({ ...n, x: baseX + idx * offset, y: baseY + idx * offset });
      }
    });
    if (newNodes.length) {
      setWorkspaceNodes((prev) => [...prev, ...newNodes]);
    }
    // Do not auto-create links; workspace entities are independent by default
    setWorkspaceLinks((prev) => prev); // no-op to emphasize intention
  };

  const handleWorkspaceDrop = (e) => {
    e.preventDefault();
    const rect = workspaceRef.current.getBoundingClientRect();
    const dropPoint = { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };

    const groupPayload = e.dataTransfer.getData('application/x-kg-group');
    if (groupPayload) {
      const { groupKey } = JSON.parse(groupPayload);
      const nodes = grouped[groupKey] || [];
      addNodesToWorkspace(nodes, dropPoint);
      return;
    }
    const entityPayload = e.dataTransfer.getData('application/x-kg-entity');
    if (entityPayload) {
      const { entityId } = JSON.parse(entityPayload);
      const node = (graphData.nodes || []).find((n) => n.id === entityId);
      if (node) addNodesToWorkspace([node], dropPoint);
    }
  };

  const handleWorkspaceDragOver = (e) => {
    e.preventDefault();
  };

  const onNodeMouseDown = (node, e) => {
    // Drag node inside workspace
    const start = { x: e.clientX, y: e.clientY };
    const startPos = { x: node.x, y: node.y };
    const onMove = (ev) => {
      const dx = (ev.clientX - start.x) / zoom;
      const dy = (ev.clientY - start.y) / zoom;
      setWorkspaceNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, x: startPos.x + dx, y: startPos.y + dy } : n)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onBackgroundMouseDown = (e) => {
    setIsPanning(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onBackgroundMouseMove = (e) => {
    if (!isPanning) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onBackgroundMouseUp = () => setIsPanning(false);

  const selectedNode = workspaceNodes.find((n) => n.id === selectedNodeId) || null;

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Left toolbox */}
      <div className="col-span-3 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <div className="font-semibold text-gray-800 flex items-center gap-2"><Wrench className="w-4 h-4"/> {t.title}</div>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => setAllExpanded(true)}><ExpandAllIcon className="w-3 h-3 inline mr-1"/>{t.expandAll}</button>
            <button className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200" onClick={() => setAllExpanded(false)}><CollapseAllIcon className="w-3 h-3 inline mr-1"/>{t.collapseAll}</button>
          </div>
        </div>

        {['characters','places','objects'].map((groupKey) => (
          <div key={groupKey} className="border-b">
            <div
              className="px-3 py-2 flex items-center justify-between bg-white hover:bg-gray-50 cursor-grab"
              draggable
              onDragStart={(e) => handleGroupDragStart(groupKey, e)}
              onClick={() => setGroupsExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))}
              title={t.hintDragGroup}
            >
              <div className="font-medium text-gray-800 flex items-center gap-2">
                <GripVertical className="w-3 h-3"/> {t[groupKey]}
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{(grouped[groupKey]||[]).length} items</span>
                <span className="ml-2 text-[10px] text-gray-400">Drag to workspace</span>
              </div>
              <span className="text-xs text-gray-500">{groupsExpanded[groupKey] ? '收合' : '展開'}</span>
            </div>
            {groupsExpanded[groupKey] && (
              <div className="p-2 space-y-2">
                {(grouped[groupKey] || []).map((entity) => (
                  <div
                    key={entity.id}
                    className="p-2 rounded-lg border border-gray-200 hover:border-gray-300 bg-white cursor-grab shadow-sm hover:shadow"
                    draggable
                    onDragStart={(e) => handleEntityDragStart(entity, e)}
                    onClick={() => setSelectedNodeId(entity.id)}
                    title={t.hintDragItem}
                  >
                  <div className="text-sm font-semibold text-gray-900 tracking-wide">{entity.name}</div>
                  <div className={`text-[10px] inline-block mt-1 px-2 py-0.5 rounded-full ${getTypeChipClasses(entity.type)}`}>{entity.type}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Relationships group */}
        <div className="border-b">
          <div
            className="px-3 py-2 flex items-center justify-between bg-white hover:bg-gray-50"
            onClick={() => setGroupsExpanded((prev) => ({ ...prev, relationships: !prev.relationships }))}
          >
            <div className="font-medium text-gray-800 flex items-center gap-2"><Layers className="w-3 h-3"/> {t.relationships}</div>
            <span className="text-xs text-gray-500">{groupsExpanded.relationships ? '收合' : '展開'}</span>
          </div>
          {groupsExpanded.relationships && (
            <div className="p-2 space-y-1 max-h-40 overflow-auto">
              {relationshipList.map((r, idx) => (
                <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-300"></span>
                  {r.from} → <em className="text-gray-500">{r.type}</em> → {r.to}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Workspace */}
      <div className="col-span-6 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <div className="font-semibold text-gray-800 flex items-center gap-2"><Move className="w-4 h-4"/> Workspace</div>
          <div className="flex items-center gap-2">
            <button className="p-1 bg-gray-100 rounded hover:bg-gray-200" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}><ZoomIn className="w-4 h-4"/></button>
              <div className="text-xs text-gray-600 w-12 text-center font-medium">{Math.round(zoom*100)}%</div>
            <button className="p-1 bg-gray-100 rounded hover:bg-gray-200" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}><ZoomOut className="w-4 h-4"/></button>
          </div>
        </div>

        <div
          ref={workspaceRef}
          className="flex-1 relative overflow-hidden bg-gradient-to-br from-red-50 to-orange-50 min-h-[560px]"
          onDrop={handleWorkspaceDrop}
          onDragOver={handleWorkspaceDragOver}
          onMouseDown={onBackgroundMouseDown}
          onMouseMove={onBackgroundMouseMove}
          onMouseUp={onBackgroundMouseUp}
        >
          <div
            className="absolute inset-0"
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}
          >
            {/* No lines by default. Entities are independent for easier design. */}

            {workspaceNodes.map((n) => (
              <div
                key={n.id}
                className={`absolute bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-sm hover:shadow-md cursor-move ${selectedNodeId===n.id?'ring-2 ring-red-500':''}`}
                style={{ left: n.x - 70, top: n.y - 32, width: 160 }}
                onMouseDown={(e) => onNodeMouseDown(n, e)}
                onClick={() => setSelectedNodeId(n.id)}
                title={n.description}
              >
                <div className="px-3 pt-2 pb-2">
                  <div className="text-[13px] font-semibold text-gray-900 tracking-wide truncate">{n.name}</div>
                  <div className={`mt-1 text-[10px] inline-block px-2 py-0.5 rounded-full ${getTypeChipClasses(n.type)}`}>{n.type}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-3 border-t bg-white flex items-center justify-between">
          <div className="text-xs text-gray-500">{workspaceNodes.length} nodes</div>
          <button
            onClick={() => onRun({ nodes: workspaceNodes, links: workspaceLinks })}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <Play className="w-4 h-4"/> {t.run}
          </button>
        </div>
      </div>

      {/* Inspector */}
      <div className="col-span-3 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
          <div className="font-semibold text-gray-800 flex items-center gap-2"><Info className="w-4 h-4"/> {t.inspector}</div>
          {selectedNode && (
            <button className="p-1 bg-gray-100 rounded hover:bg-gray-200" onClick={() => setSelectedNodeId(null)}><X className="w-4 h-4"/></button>
          )}
        </div>
        <div className="p-3 text-sm">
          {selectedNode ? (
            <>
              <label className="block text-xs text-gray-500 mb-1">{t.name}</label>
              <input
                className="w-full border rounded p-2 mb-3"
                value={selectedNode.name}
                onChange={(e) => setWorkspaceNodes((prev) => prev.map((n) => n.id===selectedNode.id? { ...n, name: e.target.value } : n))}
              />
              <label className="block text-xs text-gray-500 mb-1">{t.type}</label>
              <input className="w-full border rounded p-2 mb-3" value={selectedNode.type} onChange={(e) => setWorkspaceNodes((prev) => prev.map((n) => n.id===selectedNode.id? { ...n, type: e.target.value } : n))} />
              <label className="block text-xs text-gray-500 mb-1">{t.description}</label>
              <textarea className="w-full border rounded p-2 h-24 mb-3" value={selectedNode.description || ''} onChange={(e) => setWorkspaceNodes((prev) => prev.map((n) => n.id===selectedNode.id? { ...n, description: e.target.value } : n))} />
            </>
          ) : (
            <div className="text-gray-500 text-xs">點選工作區節點以編輯屬性。</div>
          )}
          <div className="mt-4 p-2 bg-purple-50 rounded text-xs text-purple-700 flex items-center gap-2">
            <Sparkles className="w-3 h-3"/> 使用左側群組拖放到工作區；按 RUN 生成「AI Generated」。
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphToolbox;


